import { useState, useEffect } from "react";
import { useNavigate, useLocation, useSearchParams, Link } from "react-router-dom";
import axios from "axios";
import { API, useAuth } from "../App";
import { Button } from "../components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Badge } from "../components/ui/badge";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Check, Loader2, ArrowLeft, Crown, Headphones, FileText, Users, Zap, CreditCard, Mail, Lock, User, Calendar, MapPin, Sparkles, Gift, Timer, ListMusic } from "lucide-react";
import { toast } from "sonner";

export default function SubscribePage() {
  const navigate = useNavigate();
  const location = useLocation();
  const { user, setAuthUser } = useAuth();
  const [loading, setLoading] = useState(false);
  const [subscriptionStatus, setSubscriptionStatus] = useState(null);
  const [checkingStatus, setCheckingStatus] = useState(true);
  
  // Founding member state - now fetched from API
  const [foundingStatus, setFoundingStatus] = useState(null);
  const [deal, setDeal] = useState(null);
  const [countdown, setCountdown] = useState(null);
  const [selectedPlan, setSelectedPlan] = useState("founding_monthly");
  // Annual tier removed Feb 2026 — monthly-only.
  // Soft-pitch popup state
  const [showSoftPitch, setShowSoftPitch] = useState(false);
  const [softPitchDismissed, setSoftPitchDismissed] = useState(false);
  
  // Registration form state
  const [formData, setFormData] = useState({
    name: "",
    email: "",
    password: "",
    confirmPassword: "",
  });
  const [formErrors, setFormErrors] = useState({});
  // Click-wrap: user must actively agree to Terms + Privacy before purchasing
  const [agreedToTerms, setAgreedToTerms] = useState(false);

  // Promo code state — auto-prefilled from ?code=XYZ in URL (campaign links)
  const [promoCode, setPromoCode] = useState("");
  const [promoValidation, setPromoValidation] = useState(null);
  const [validatingPromo, setValidatingPromo] = useState(false);
  const [searchParams] = useSearchParams();

  useEffect(() => {
    fetchData();
  }, [user]);

  // Pre-fill promo code from URL (?code=HALFOFF) so email-campaign clicks
  // arrive at checkout with the discount already populated.
  useEffect(() => {
    const codeFromUrl = (searchParams.get("code") || "").trim().toUpperCase();
    if (codeFromUrl && !promoCode) {
      setPromoCode(codeFromUrl);
      // Auto-validate so the user sees the discount confirmed immediately
      handleValidatePromo(codeFromUrl);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchParams]);

  const handleValidatePromo = async (codeOverride) => {
    const code = (codeOverride ?? promoCode).trim().toUpperCase();
    if (!code) {
      setPromoValidation(null);
      return;
    }
    setValidatingPromo(true);
    try {
      const r = await axios.post(`${API}/promo/validate`, {
        code,
        cart_total: 9.99,
      });
      setPromoValidation(r.data);
      if (r.data?.valid) {
        toast.success(`Promo applied: ${r.data.discount_value}% off your first month`);
      } else {
        toast.error(r.data?.error || "Invalid promo code");
      }
    } catch (e) {
      setPromoValidation({ valid: false, error: "Could not validate code" });
      toast.error("Could not validate code");
    } finally {
      setValidatingPromo(false);
    }
  };

  const fetchData = async () => {
    try {
      // Fetch subscription plans and founding member status
      const plansRes = await axios.get(`${API}/subscription/plans`);
      setFoundingStatus(plansRes.data.founding_member_status);
      setDeal(plansRes.data.deal);
      
      // Set default plan based on founding availability
      if (plansRes.data.founding_member_status?.available) {
        setSelectedPlan("founding_monthly");
      } else {
        setSelectedPlan("unlimited_monthly");
      }
      
      if (user) {
        const statusRes = await axios.get(`${API}/subscription/status`);
        setSubscriptionStatus(statusRes.data);
      }
    } catch (error) {
      console.error("Failed to fetch data", error);
    } finally {
      setCheckingStatus(false);
    }
  };

  // Countdown timer for deal
  useEffect(() => {
    if (!deal?.active || !deal?.end_date) return;
    const tick = () => {
      const end = new Date(deal.end_date).getTime();
      const diff = end - Date.now();
      if (diff <= 0) { setCountdown(null); return; }
      const days = Math.floor(diff / (1000 * 60 * 60 * 24));
      const hours = Math.floor((diff / (1000 * 60 * 60)) % 24);
      const mins = Math.floor((diff / (1000 * 60)) % 60);
      setCountdown({ days, hours, mins });
    };
    tick();
    const interval = setInterval(tick, 60000);
    return () => clearInterval(interval);
  }, [deal]);

  // Soft-pitch popup: trigger 5 seconds after the page loads (every visit) if the
  // visitor hasn't already started checkout. We intentionally do NOT use sessionStorage
  // here — a hesitant visitor who clicks away and comes back should see it again.
  useEffect(() => {
    if (user && subscriptionStatus?.has_subscription) return;
    if (softPitchDismissed) return;
    const t = setTimeout(() => setShowSoftPitch(true), 5000);
    return () => clearTimeout(t);
  }, [user, subscriptionStatus, softPitchDismissed]);


  // Annual cycle removed - monthly is the only option now.

  const validateForm = () => {
    const errors = {};
    
    if (!formData.name.trim()) errors.name = "Name is required";
    if (!formData.email.trim()) errors.email = "Email is required";
    else if (!/\S+@\S+\.\S+/.test(formData.email)) errors.email = "Invalid email format";
    if (!formData.password) errors.password = "Password is required";
    else if (formData.password.length < 8) errors.password = "Password must be at least 8 characters";
    if (formData.password !== formData.confirmPassword) errors.confirmPassword = "Passwords don't match";
    
    setFormErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
    // Clear error when user starts typing
    if (formErrors[name]) {
      setFormErrors(prev => ({ ...prev, [name]: "" }));
    }
  };

  const handleSubscribe = async () => {
    if (!agreedToTerms) {
      toast.error("Please agree to the Terms of Service and Privacy Policy to continue.");
      return;
    }
    // If user is logged in, go directly to Stripe
    if (user) {
      if (subscriptionStatus?.has_subscription) {
        toast.info("You already have an active subscription");
        navigate("/library");
        return;
      }
      await createSubscription();
      return;
    }

    // If not logged in, validate form and register first
    if (!validateForm()) {
      toast.error("Please fill in all required fields correctly");
      return;
    }

    setLoading(true);
    try {
      // Step 1: Register the user
      const registerResponse = await axios.post(`${API}/auth/register`, {
        name: formData.name,
        email: formData.email,
        password: formData.password,
        referral_code: new URLSearchParams(window.location.search).get("ref") || undefined
      });

      // Step 2: Set auth state
      if (registerResponse.data.token) {
        setAuthUser(registerResponse.data.user, registerResponse.data.token);
        toast.success("Account created! Redirecting to payment...");
        
        // Step 3: Create subscription checkout - founder status auto-applied by backend
        const subscriptionPayload = {
          plan_id: selectedPlan,
          origin_url: window.location.origin,
          promo_code: promoValidation?.valid ? promoCode.trim().toUpperCase() : undefined,
        };
        
        const subResponse = await axios.post(`${API}/subscription/create`, subscriptionPayload, { 
          
          headers: { Authorization: `Bearer ${registerResponse.data.token}` }
        });

        if (subResponse.data.url) {
          window.location.href = subResponse.data.url;
        }
      }
    } catch (error) {
      console.error("Registration/subscription error:", error);
      const errorMessage = error.response?.data?.detail;
      if (typeof errorMessage === 'string') {
        toast.error(errorMessage);
      } else if (Array.isArray(errorMessage)) {
        toast.error(errorMessage[0]?.msg || "Registration failed");
      } else {
        toast.error("Failed to create account. Please try again.");
      }
      setLoading(false);
    }
  };

  const createSubscription = async () => {
    setLoading(true);
    try {
      // Founder status auto-applied by backend based on available spots
      const subscriptionPayload = {
        plan_id: selectedPlan,
        origin_url: window.location.origin,
        promo_code: promoValidation?.valid ? promoCode.trim().toUpperCase() : undefined,
      };
      
      const response = await axios.post(`${API}/subscription/create`, subscriptionPayload);

      if (response.data.url) {
        window.location.href = response.data.url;
      }
    } catch (error) {
      console.error("Subscription error:", error);
      toast.error(error.response?.data?.detail || "Failed to create subscription");
      setLoading(false);
    }
  };

  if (checkingStatus) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <Loader2 className="h-8 w-8 animate-spin text-gold" />
      </div>
    );
  }

  if (subscriptionStatus?.has_subscription) {
    return (
      <div className="min-h-screen py-12">
        <div className="container mx-auto px-4 max-w-2xl">
          <Card className="text-center">
            <CardContent className="py-12">
              <div className="w-16 h-16 rounded-full bg-gold/20 flex items-center justify-center mx-auto mb-6">
                <Check className="h-8 w-8 text-gold" />
              </div>
              <h2 className="font-serif text-2xl font-bold mb-4">You're Already Subscribed!</h2>
              <p className="text-muted-foreground mb-6">
                You have full access to all our content. Head to your library to start learning.
              </p>
              <div className="flex justify-center gap-4">
                <Button onClick={() => navigate("/library")} className="bg-gold text-primary-foreground hover:bg-gold/90">
                  Go to Library
                </Button>
                <Button variant="outline" onClick={() => navigate("/account")}>
                  Manage Subscription
                </Button>
              </div>
            </CardContent>
          </Card>
        </div>
      </div>
    );
  }

  const features = [
    { icon: Headphones, text: "Unlimited audio streaming" },
    { icon: FileText, text: "Unlimited PDF flowchart downloads" },
    { icon: ListMusic, text: "Create your own playlists" },
    { icon: Users, text: "Join our community" },
    { icon: Zap, text: "All courses: 1L, 2L, 3L + Bar Prep (coming soon)" },
    { icon: Crown, text: "Progress tracking and favorites" },
  ];

  // Determine current pricing based on founding availability
  const isFoundingAvailable = foundingStatus?.available;
  const spotsRemaining = foundingStatus?.spots_remaining || 0;

  // Monthly pricing only - annual tier was removed Feb 2026
  const REGULAR_MONTHLY = 9.99;
  const FOUNDING_MONTHLY = 5.99;
  const dealMonthlyPrice = deal?.active ? deal.deal_price : FOUNDING_MONTHLY;
  const currentPrice = isFoundingAvailable ? dealMonthlyPrice : REGULAR_MONTHLY;
  const priceInterval = "month";

  return (
    <div className="min-h-screen py-12" data-testid="subscribe-page">
      {/* Soft-pitch popup — appears 5s after page load if visitor hasn't started checkout */}
      {showSoftPitch && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-sm animate-in fade-in duration-200"
          data-testid="subscribe-soft-pitch-overlay"
          onClick={() => { setShowSoftPitch(false); setSoftPitchDismissed(true); }}
        >
          <div
            className="relative max-w-md w-[92%] mx-4 bg-card border border-primary/30 rounded-2xl p-6 md:p-7 shadow-2xl"
            onClick={(e) => e.stopPropagation()}
            data-testid="subscribe-soft-pitch-modal"
          >
            <button
              type="button"
              className="absolute top-3 right-3 text-muted-foreground hover:text-foreground text-xl leading-none"
              onClick={() => { setShowSoftPitch(false); setSoftPitchDismissed(true); }}
              aria-label="Dismiss"
              data-testid="subscribe-soft-pitch-close"
            >
              ×
            </button>
            <div className="text-center mb-4">
              <div className="inline-flex items-center justify-center h-12 w-12 rounded-full bg-primary/15 mb-2">
                <Headphones className="h-6 w-6 text-primary" />
              </div>
              <h3 className="font-serif text-xl md:text-2xl font-bold leading-tight">
                Not ready to subscribe?
              </h3>
              <p className="text-sm text-muted-foreground mt-2">
                Try Bar Songz another way — no commitment.
              </p>
            </div>
            <div className="space-y-3">
              <Button
                onClick={() => {
                  setShowSoftPitch(false);
                  setSoftPitchDismissed(true);
                  navigate("/#sample-player");
                  setTimeout(() => {
                    const el = document.getElementById("sample-player");
                    if (el) el.scrollIntoView({ behavior: "smooth" });
                  }, 400);
                }}
                className="w-full justify-start gap-3 bg-primary text-primary-foreground hover:bg-primary/90 rounded-xl h-auto py-3 px-4"
                data-testid="soft-pitch-free-song-btn"
              >
                <Gift className="h-5 w-5 flex-shrink-0" />
                <div className="text-left">
                  <p className="font-semibold">Get a Free Song</p>
                  <p className="text-xs opacity-90">One full track delivered to your inbox.</p>
                </div>
              </Button>
              <Button
                onClick={() => {
                  setShowSoftPitch(false);
                  setSoftPitchDismissed(true);
                  navigate("/packs");
                }}
                variant="outline"
                className="w-full justify-start gap-3 rounded-xl h-auto py-3 px-4 hover:bg-primary/5"
                data-testid="soft-pitch-browse-packs-btn"
              >
                <Sparkles className="h-5 w-5 text-primary flex-shrink-0" />
                <div className="text-left">
                  <p className="font-semibold">Browse Course Packs · from $25</p>
                  <p className="text-xs text-muted-foreground">Own your study tracks — download forever.</p>
                </div>
              </Button>
              <button
                type="button"
                onClick={() => { setShowSoftPitch(false); setSoftPitchDismissed(true); }}
                className="w-full text-xs text-muted-foreground hover:text-foreground transition-colors py-2"
                data-testid="soft-pitch-dismiss-btn"
              >
                No thanks, keep showing the subscription plan
              </button>
            </div>
          </div>
        </div>
      )}

      <div className="container mx-auto px-4 max-w-5xl">
        {/* Back Button */}
        <Button 
          variant="ghost" 
          className="mb-6 text-muted-foreground hover:text-foreground"
          onClick={() => navigate(-1)}
          data-testid="back-btn"
        >
          <ArrowLeft className="mr-2 h-4 w-4" />
          Back
        </Button>

        {/* Founding Member Banner */}
        {isFoundingAvailable && (
          <div className="mb-8 p-4 bg-gradient-to-r from-amber-500/20 to-yellow-500/20 border border-amber-500/30 rounded-xl text-center">
            {/* Deal countdown */}
            {deal?.active && countdown && (
              <div className="mb-3 pb-3 border-b border-amber-500/20">
                <div className="flex items-center justify-center gap-2 mb-2">
                  <Timer className="h-4 w-4 text-amber-400" />
                  <span className="text-sm font-semibold text-amber-400">{deal.label}</span>
                </div>
                <div className="flex justify-center gap-4">
                  {[
                    { val: countdown.days, label: "days" },
                    { val: countdown.hours, label: "hrs" },
                    { val: countdown.mins, label: "min" },
                  ].map((t, i) => (
                    <div key={i} className="text-center">
                      <span className="text-2xl font-bold text-foreground">{t.val}</span>
                      <p className="text-[10px] text-muted-foreground uppercase tracking-wider">{t.label}</p>
                    </div>
                  ))}
                </div>
              </div>
            )}
            <div className="flex items-center justify-center gap-2 mb-2">
              <Gift className="h-5 w-5 text-amber-500" />
              <span className="font-bold text-amber-500">Founding Member Special!</span>
              <Gift className="h-5 w-5 text-amber-500" />
            </div>
            <p className="text-lg font-semibold">
              Get <span className="text-amber-500">${deal?.active ? deal.deal_price : FOUNDING_MONTHLY}/month</span>{deal?.active ? ' — limited time!' : ' — locked in for life!'}
            </p>
            <p className="text-sm text-muted-foreground mt-1" data-testid="founding-banner-subtext">
              {foundingStatus?.returning_founder
                ? "Welcome back! As a Founding Member, your founding rate is still yours."
                : <>Only <span className="font-bold text-amber-500">{spotsRemaining}</span> of 100 founding member spots remaining</>}
            </p>
            {deal?.active && (
              <p className="text-xs text-muted-foreground mt-1">
                After deal ends, founding price returns to ${deal.original_price}/mo
              </p>
            )}
          </div>
        )}

        <div className="text-center mb-12">
          <Badge className={`mb-4 ${isFoundingAvailable ? 'bg-amber-500/20 text-amber-500 border-amber-500' : 'bg-gold/20 text-gold border-gold'}`}>
            {isFoundingAvailable ? (
              <><Sparkles className="h-3 w-3 mr-1" /> Founding Member Pricing</>
            ) : (
              <><Crown className="h-3 w-3 mr-1" /> Simple Pricing</>
            )}
          </Badge>
          <h1 className="font-serif text-4xl font-bold mb-4">One Plan. Full Access.</h1>
          <p className="text-muted-foreground text-lg">
            No tiers, no limits, no confusion. Just everything you need to succeed.
          </p>
          {foundingStatus?.enrollment_closed && !isFoundingAvailable && (
            <p className="mt-3 text-sm text-muted-foreground" data-testid="founding-enrollment-closed-note">
              Founding Member enrollment is now closed. Thank you to our founders!
            </p>
          )}
        </div>

        <div className="grid md:grid-cols-2 gap-8 max-w-4xl mx-auto">
          {/* Plan Details Card */}
          <Card className={`border-gold ring-2 ${isFoundingAvailable ? 'ring-amber-500/50' : 'ring-gold/30'}`} data-testid="plan-card">
            <CardHeader className="text-center pb-2">
              {/* Founding Member Badge */}
              {isFoundingAvailable && (
                <Badge className="mb-3 bg-gradient-to-r from-amber-500 to-yellow-500 text-black border-0 animate-pulse">
                  <Sparkles className="h-3 w-3 mr-1" /> {foundingStatus?.returning_founder ? "Founding Member Rate" : `${spotsRemaining} Founding Spots Left!`}
                </Badge>
              )}
              
              <div className="flex items-center justify-center gap-2 mb-2">
                <Crown className="h-6 w-6 text-gold" />
                <CardTitle className="font-serif text-2xl">Full Access</CardTitle>
              </div>

              {/* Price Display */}
              <div className="mt-4">
                {isFoundingAvailable && (
                  <span className="text-2xl text-muted-foreground line-through mr-2">${REGULAR_MONTHLY}</span>
                )}
                <span className="text-5xl font-bold text-gold">${currentPrice}</span>
                <span className="text-muted-foreground text-lg">/{priceInterval}</span>
              </div>

              {/* Savings message */}
              {isFoundingAvailable && (
                <p className="text-sm text-amber-500 font-medium mt-2">
                  {deal?.active
                    ? `$${deal.deal_price}/month — limited time launch offer`
                    : `$${FOUNDING_MONTHLY}/month, locked in for life`}
                </p>
              )}

              <p className="text-sm text-muted-foreground mt-2">
                Cancel anytime
              </p>
            </CardHeader>
            <CardContent className="pt-6">
              {/* Features List */}
              <ul className="space-y-4">
                {features.map((feature, i) => (
                  <li key={i} className="flex items-center gap-3">
                    <div className="w-8 h-8 rounded-full bg-gold/20 flex items-center justify-center flex-shrink-0">
                      <feature.icon className="h-4 w-4 text-gold" />
                    </div>
                    <span className="text-sm">{feature.text}</span>
                  </li>
                ))}
                {isFoundingAvailable && (
                  <li className="flex items-center gap-3">
                    <div className="w-8 h-8 rounded-full bg-amber-500/20 flex items-center justify-center flex-shrink-0">
                      <Sparkles className="h-4 w-4 text-amber-500" />
                    </div>
                    <span className="text-sm font-medium text-amber-500">Exclusive Founding Member badge</span>
                  </li>
                )}
              </ul>

              {/* Suggestion box callout */}
              <div className="mt-4 p-3 bg-gold/10 border border-gold/30 rounded-lg">
                <div className="flex items-center gap-2 text-sm">
                  <Gift className="h-4 w-4 text-gold" />
                  <span className="font-medium text-gold">Shape the Platform!</span>
                </div>
                <p className="text-xs text-muted-foreground mt-1">
                  As a subscriber, you can submit suggestions for new topics and features in our Community section.
                </p>
              </div>

              {/* Growing Library callout */}
              <div className="mt-3 p-3 bg-primary/5 border border-gold/20 rounded-lg">
                <div className="flex items-center gap-2 text-sm">
                  <Zap className="h-4 w-4 text-gold" />
                  <span className="font-medium">Growing Library!</span>
                </div>
                <p className="text-xs text-muted-foreground mt-1">
                  2L, 3L courses & PDF flowcharts are in development. Subscribe now to get instant access when released!
                </p>
              </div>

              <div className="mt-4 p-4 bg-muted/50 rounded-lg">
                <div className="flex items-center gap-2 text-sm">
                  <CreditCard className="h-4 w-4 text-gold" />
                  <span className="font-medium">Secure payment via Stripe</span>
                </div>
                <p className="text-xs text-muted-foreground mt-1">
                  You'll be charged immediately. Cancel anytime.
                </p>
              </div>
            </CardContent>
          </Card>

          {/* Registration/Action Card */}
          <Card data-testid="signup-card">
            <CardHeader>
              <CardTitle className="font-serif text-xl flex items-center gap-2">
                {user ? (
                  <>
                    <Check className="h-5 w-5 text-green-500" />
                    Ready to Subscribe
                  </>
                ) : (
                  <>
                    <User className="h-5 w-5 text-gold" />
                    Create Your Account
                  </>
                )}
              </CardTitle>
            </CardHeader>
            <CardContent>
              {user ? (
                // User is logged in - show simple subscribe button
                <div className="space-y-4">
                  <div className="p-4 bg-muted/50 rounded-lg">
                    <p className="text-sm">Signed in as:</p>
                    <p className="font-medium">{user.email}</p>
                  </div>
                  
                  {isFoundingAvailable && (
                    <div className="p-3 bg-gradient-to-r from-amber-500/10 to-yellow-500/10 border border-amber-500/30 rounded-lg">
                      <p className="text-sm font-medium text-amber-500 flex items-center gap-2">
                        <Sparkles className="h-4 w-4" />
                        You're getting Founding Member pricing!
                      </p>
                      <p className="text-xs text-muted-foreground mt-1">
                        You'll be charged ${dealMonthlyPrice}/month{deal?.active ? ` (limited time deal), then $${FOUNDING_MONTHLY}/month` : ', locked in for life'}
                      </p>
                    </div>
                  )}
                  
                  <label
                    className="flex items-start gap-2 text-sm text-muted-foreground cursor-pointer select-none"
                    data-testid="subscribe-terms-acceptance-logged-in"
                  >
                    <input
                      type="checkbox"
                      checked={agreedToTerms}
                      onChange={(e) => setAgreedToTerms(e.target.checked)}
                      className="mt-1 h-4 w-4 accent-gold cursor-pointer flex-shrink-0"
                      data-testid="subscribe-terms-checkbox-logged-in"
                    />
                    <span>
                      I have read and agree to the{" "}
                      <Link to="/terms" target="_blank" className="text-gold underline hover:opacity-80">
                        Terms of Service
                      </Link>{" "}
                      and{" "}
                      <Link to="/privacy" target="_blank" className="text-gold underline hover:opacity-80">
                        Privacy Policy
                      </Link>
                      . I understand Bar Songz does not guarantee any law-school or bar-exam outcome
                      and does not provide legal advice.
                    </span>
                  </label>

                  {/* Promo code input — auto-pre-filled from ?code= URL param */}
                  <div className="space-y-1.5" data-testid="promo-code-section">
                    <label className="text-xs font-bold uppercase tracking-wider text-muted-foreground">
                      Promo code
                      <span className="font-normal normal-case tracking-normal ml-1 text-foreground/60">(optional)</span>
                    </label>
                    <div className="flex gap-2">
                      <input
                        type="text"
                        value={promoCode}
                        onChange={(e) => {
                          setPromoCode(e.target.value.toUpperCase());
                          if (promoValidation) setPromoValidation(null);
                        }}
                        placeholder="e.g. HALFOFF"
                        className="flex-1 h-10 px-3 rounded-md border border-border bg-background text-sm font-mono uppercase tracking-wider focus:outline-none focus:ring-2 focus:ring-primary/50"
                        data-testid="promo-code-input"
                      />
                      <Button
                        type="button"
                        variant="outline"
                        size="sm"
                        onClick={() => handleValidatePromo()}
                        disabled={!promoCode.trim() || validatingPromo}
                        className="h-10 border-primary text-primary hover:bg-primary hover:text-primary-foreground"
                        data-testid="promo-code-apply-btn"
                      >
                        {validatingPromo ? <Loader2 className="h-4 w-4 animate-spin" /> : "Apply"}
                      </Button>
                    </div>
                    {promoValidation?.valid && (
                      <p className="text-xs text-green-600 font-medium" data-testid="promo-code-success">
                        ✓ {promoValidation.discount_value}% off your first month applied
                      </p>
                    )}
                    {promoValidation && !promoValidation.valid && (
                      <p className="text-xs text-red-500">{promoValidation.error || "Invalid code"}</p>
                    )}
                  </div>

                  <Button 
                    size="lg"
                    className={`w-full font-serif text-lg h-14 ${isFoundingAvailable 
                      ? 'bg-gradient-to-r from-amber-500 to-yellow-500 text-black hover:from-amber-600 hover:to-yellow-600' 
                      : 'bg-gold text-primary-foreground hover:bg-gold/90'}`}
                    onClick={handleSubscribe}
                    disabled={loading || !agreedToTerms}
                    data-testid="subscribe-btn"
                  >
                    {loading ? (
                      <>
                        <Loader2 className="mr-2 h-5 w-5 animate-spin" />
                        Processing...
                      </>
                    ) : (
                      <>
                        Subscribe Now
                        {isFoundingAvailable ? <Sparkles className="ml-2 h-5 w-5" /> : <Crown className="ml-2 h-5 w-5" />}
                      </>
                    )}
                  </Button>
                  <p className="text-center text-xs text-muted-foreground">
                    Not you?{" "}
                    <span 
                      className="text-gold hover:underline cursor-pointer"
                      onClick={() => navigate("/login")}
                    >
                      Sign in with a different account
                    </span>
                  </p>
                </div>
              ) : (
                // User not logged in - show registration form
                <form onSubmit={(e) => { e.preventDefault(); handleSubscribe(); }} className="space-y-4">
                  {/* Name */}
                  <div className="space-y-2">
                    <Label htmlFor="name" className="flex items-center gap-2">
                      <User className="h-3 w-3" /> Full Name
                    </Label>
                    <Input
                      id="name"
                      name="name"
                      placeholder="John Doe"
                      value={formData.name}
                      onChange={handleInputChange}
                      className={formErrors.name ? "border-red-500" : ""}
                      data-testid="name-input"
                    />
                    {formErrors.name && <p className="text-xs text-red-500">{formErrors.name}</p>}
                  </div>

                  {/* Email */}
                  <div className="space-y-2">
                    <Label htmlFor="email" className="flex items-center gap-2">
                      <Mail className="h-3 w-3" /> Email
                    </Label>
                    <Input
                      id="email"
                      name="email"
                      type="email"
                      placeholder="you@lawschool.edu"
                      value={formData.email}
                      onChange={handleInputChange}
                      className={formErrors.email ? "border-red-500" : ""}
                      data-testid="email-input"
                    />
                    {formErrors.email && <p className="text-xs text-red-500">{formErrors.email}</p>}
                  </div>

                  {/* Password */}
                  <div className="grid grid-cols-2 gap-3">
                    <div className="space-y-2">
                      <Label htmlFor="password" className="flex items-center gap-2">
                        <Lock className="h-3 w-3" /> Password
                      </Label>
                      <Input
                        id="password"
                        name="password"
                        type="password"
                        placeholder="••••••••"
                        value={formData.password}
                        onChange={handleInputChange}
                        className={formErrors.password ? "border-red-500" : ""}
                        data-testid="password-input"
                      />
                      {formErrors.password && <p className="text-xs text-red-500">{formErrors.password}</p>}
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="confirmPassword">Confirm</Label>
                      <Input
                        id="confirmPassword"
                        name="confirmPassword"
                        type="password"
                        placeholder="••••••••"
                        value={formData.confirmPassword}
                        onChange={handleInputChange}
                        className={formErrors.confirmPassword ? "border-red-500" : ""}
                        data-testid="confirm-password-input"
                      />
                      {formErrors.confirmPassword && <p className="text-xs text-red-500">{formErrors.confirmPassword}</p>}
                    </div>
                  </div>

                  {/* Founding Member Badge for non-logged in users */}
                  {isFoundingAvailable && (
                    <div className="p-3 bg-gradient-to-r from-amber-500/10 to-yellow-500/10 border border-amber-500/30 rounded-lg">
                      <p className="text-sm font-medium text-amber-500 flex items-center gap-2">
                        <Sparkles className="h-4 w-4" />
                        You're getting Founding Member pricing!
                      </p>
                    </div>
                  )}

                  <label
                    className="flex items-start gap-2 text-sm text-muted-foreground cursor-pointer select-none"
                    data-testid="subscribe-terms-acceptance-new"
                  >
                    <input
                      type="checkbox"
                      checked={agreedToTerms}
                      onChange={(e) => setAgreedToTerms(e.target.checked)}
                      className="mt-1 h-4 w-4 accent-gold cursor-pointer flex-shrink-0"
                      data-testid="subscribe-terms-checkbox-new"
                    />
                    <span>
                      I have read and agree to the{" "}
                      <Link to="/terms" target="_blank" className="text-gold underline hover:opacity-80">
                        Terms of Service
                      </Link>{" "}
                      and{" "}
                      <Link to="/privacy" target="_blank" className="text-gold underline hover:opacity-80">
                        Privacy Policy
                      </Link>
                      . I understand Bar Songz does not guarantee any law-school or bar-exam outcome
                      and does not provide legal advice.
                    </span>
                  </label>

                  {/* Promo code (registration path) */}
                  <div className="space-y-1.5" data-testid="promo-code-section-register">
                    <label className="text-xs font-bold uppercase tracking-wider text-muted-foreground">
                      Promo code
                      <span className="font-normal normal-case tracking-normal ml-1 text-foreground/60">(optional)</span>
                    </label>
                    <div className="flex gap-2">
                      <input
                        type="text"
                        value={promoCode}
                        onChange={(e) => {
                          setPromoCode(e.target.value.toUpperCase());
                          if (promoValidation) setPromoValidation(null);
                        }}
                        placeholder="e.g. HALFOFF"
                        className="flex-1 h-10 px-3 rounded-md border border-border bg-background text-sm font-mono uppercase tracking-wider focus:outline-none focus:ring-2 focus:ring-primary/50"
                        data-testid="promo-code-input-register"
                      />
                      <Button
                        type="button"
                        variant="outline"
                        size="sm"
                        onClick={() => handleValidatePromo()}
                        disabled={!promoCode.trim() || validatingPromo}
                        className="h-10 border-primary text-primary hover:bg-primary hover:text-primary-foreground"
                      >
                        {validatingPromo ? <Loader2 className="h-4 w-4 animate-spin" /> : "Apply"}
                      </Button>
                    </div>
                    {promoValidation?.valid && (
                      <p className="text-xs text-green-600 font-medium">
                        ✓ {promoValidation.discount_value}% off your first month applied
                      </p>
                    )}
                    {promoValidation && !promoValidation.valid && (
                      <p className="text-xs text-red-500">{promoValidation.error || "Invalid code"}</p>
                    )}
                  </div>

                  {/* Submit Button */}
                  <Button 
                    type="submit"
                    size="lg"
                    className={`w-full font-serif text-lg h-14 ${isFoundingAvailable 
                      ? 'bg-gradient-to-r from-amber-500 to-yellow-500 text-black hover:from-amber-600 hover:to-yellow-600' 
                      : 'bg-gold text-primary-foreground hover:bg-gold/90'}`}
                    disabled={loading || !agreedToTerms}
                    data-testid="subscribe-btn"
                  >
                    {loading ? (
                      <>
                        <Loader2 className="mr-2 h-5 w-5 animate-spin" />
                        Creating Account...
                      </>
                    ) : (
                      <>
                        <CreditCard className="mr-2 h-5 w-5" />
                        Continue to Payment
                      </>
                    )}
                  </Button>

                  <p className="text-center text-xs text-muted-foreground">
                    Already have an account?{" "}
                    <span 
                      className="text-gold hover:underline cursor-pointer"
                      onClick={() => navigate("/login", { state: { from: location } })}
                    >
                      Sign in
                    </span>
                  </p>
                </form>
              )}
            </CardContent>
          </Card>
        </div>

        {/* FAQ or Trust */}
        <div className="mt-12 text-center">
          <p className="text-muted-foreground">
            Questions? Check out our{" "}
            <span 
              className="text-gold hover:underline cursor-pointer"
              onClick={() => navigate("/community")}
            >
              community forum
            </span>
            {" "}or reach out anytime.
          </p>
        </div>
      </div>
    </div>
  );
}
